import {
	Button,
	Head,
	IHelpContent,
	ListIcon,
	MapIcon,
	RouteSelection,
	TollSectionMap,
	Wrapper,
	renderErrorMessage,
} from '@adac-mp/components';
import { withTracking } from '@adac-mp/components/src/utils/tracking/tracking';
import {
	ITollSectionOffer,
	IOfferContext,
	OfferContext,
	VignetteContext,
	ITypeVignetteOrderingProcessFields,
	getLabelValue,
	getUrlSearchParamsAsStack,
	preSelectSectionTolls,
	tollSectionIsDisabled,
} from '@adac-mp/shared';
import React, { useContext, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

interface ITollRoutesSelectionProps {
	hasError?: boolean;
	helpContent?: IHelpContent;
	updateValidatedStepsMap?: (hasError: boolean, reset?: boolean) => void;
}

const TollRoutesSelectionComponent = ({
	hasError,
	helpContent,
	updateValidatedStepsMap = () => {},
}: ITollRoutesSelectionProps) => {
	const [searchParams] = useSearchParams();
	const { tollSectionOffers, labels } = useContext<IOfferContext>(OfferContext);
	const {
		productsTypeMap,
		selectedProducts,
		selectedSectionTolls,
		contentfulLabels,
		addSelectedSectionTolls,
		updateSelectedSectionTolls,
		removeSelectedSectionTollsById,
	} = useContext(VignetteContext);
	const currentLabels = contentfulLabels as ITypeVignetteOrderingProcessFields;
	const [uniqueSections, setUniqueSections] = useState<ITollSectionOffer[] | undefined>();
	const [mapView, setMapView] = useState<boolean>(false);
	const hasSectionToll = productsTypeMap.get('sectionToll');

	useEffect(() => {
		if (tollSectionOffers.length > 0) {
			const currUniqueSections = tollSectionOffers
				.filter(({ moduleTypeId }) => moduleTypeId === 3)
				.filter(
					(section, index) =>
						index ===
						tollSectionOffers.findIndex((obj) => section.serviceCatalogName === obj.serviceCatalogName),
				);
			setUniqueSections(currUniqueSections);
			setMapView(false);
			if (selectedSectionTolls && selectedSectionTolls.length > 0) {
				updateSelectedSectionTolls(tollSectionOffers);
			}
		}
	}, [tollSectionOffers]);

	useEffect(() => {
		const urlPreSelectedSectionTolls = getUrlSearchParamsAsStack('section-tolls', searchParams);
		const preSelectedSectionTolls = preSelectSectionTolls(tollSectionOffers, urlPreSelectedSectionTolls);
		if (preSelectedSectionTolls) {
			preSelectedSectionTolls.forEach((sectionToll) => {
				if (sectionToll && !tollSectionIsDisabled(sectionToll.downtimes)) {
					addSelectedSectionTolls(sectionToll);
				}
			});
		}
	}, []);

	useEffect(() => {
		updateValidatedStepsMap(!Boolean(hasSectionToll && selectedSectionTolls.length === 0));
	}, [selectedProducts, selectedSectionTolls]);

	return (
		<Wrapper animate>
			<Head
				headline={currentLabels?.headlineTollSection || 'Wählen Sie eine oder mehrere Strecken'}
				helpContent={helpContent}
			/>
			{hasError &&
				renderErrorMessage({
					message: currentLabels?.errorTollSection || 'Bitte wählen Sie eine Strecke aus',
					className: 'mb-5 w-full mx-auto md:w-6/12',
				})}
			{mapView ? (
				<TollSectionMap labels={labels} contentfulLabels={currentLabels} />
			) : (
				<div className="w-full flex flex-col md:items-stretch md:self-center md:w-6/12 md:basis-full rounded gap-px bg-gray-600">
					{uniqueSections?.map((section) => {
						const correspondingTollSections = tollSectionOffers.filter(
							(offer) => offer.serviceCatalogName === section?.serviceCatalogName,
						);
						return (
							<RouteSelection
								key={section.serviceCatalogId}
								route={section}
								correspondingTollSections={correspondingTollSections}
								selectedVignetteTollSections={selectedSectionTolls}
								labels={labels}
								contentfulLabels={contentfulLabels as ITypeVignetteOrderingProcessFields}
								addSelectedVignetteTollSections={addSelectedSectionTolls}
								removeSelectedVignetteTollSectionsById={removeSelectedSectionTollsById}
							/>
						);
					})}
				</div>
			)}
			<Button
				variant="secondary"
				size="sm"
				className="w-fit self-center mt-4 mb-3 px-1.5"
				onClick={() => {
					setMapView((prevState) => !prevState);
				}}>
				{mapView ? <ListIcon /> : <MapIcon />}
				<span className="ml-2">
					{mapView
						? getLabelValue('mp.tollSection.listView', labels) || 'Auswahl über Listenansicht'
						: getLabelValue('mp.tollSection.mapView', labels) || 'Auswahl über Kartenansicht'}
				</span>
			</Button>
		</Wrapper>
	);
};

export const TollRoutesSelection = withTracking(TollRoutesSelectionComponent);
